import React, { useEffect } from 'react';
import { createPortal } from 'react-dom';
import { ChevronRight, X } from 'lucide-react';

/**
 * Full-screen mobile drawer that overlays the viewport when the hamburger is
 * tapped. Independent of the navbar's height/position so we don't have to
 * chase trust-bar and fixed/sticky positioning states.
 *
 * Behaviour:
 *  - Slides in from the top with fade + translate
 *  - Locks body scroll while open
 *  - Sticky "Request a Demo" CTA pinned to the bottom
 *  - Close via top-right X button or by picking a link
 */
const MobileMenu = ({ isOpen, links, onAnchor, onPage, onCtaClick, onClose }) => {
  useEffect(() => {
    if (!isOpen) return undefined;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = prev; };
  }, [isOpen]);

  return createPortal(
    <div
      role="dialog"
      aria-modal="true"
      aria-hidden={!isOpen}
      data-testid="mobile-menu-panel"
      className={`lg:hidden fixed inset-0 z-[60] bg-white flex flex-col transition-all duration-300 ease-out ${
        isOpen ? 'opacity-100 translate-y-0 pointer-events-auto' : 'opacity-0 -translate-y-4 pointer-events-none'
      }`}
    >
      {/* Header bar inside the drawer — logo + close */}
      <div className="flex-shrink-0 flex items-center justify-between px-5 py-4 border-b border-gray-100">
        <img
          src="/imprint-logo-color.svg"
          alt="Imprint M.I.S."
          className="h-10 w-auto"
        />
        <button
          type="button"
          onClick={onClose}
          data-testid="mobile-menu-close"
          aria-label="Close menu"
          className="w-10 h-10 rounded-full border border-gray-200 flex items-center justify-center text-gray-600 hover:text-[#00BCE4] hover:border-[#00BCE4] transition-colors"
        >
          <X size={20} />
        </button>
      </div>

      {/* Scrollable link list */}
      <nav
        className="flex-1 overflow-y-auto overscroll-contain px-5 py-4"
        style={{ WebkitOverflowScrolling: 'touch' }}
      >
        <ul className="space-y-1">
          {links.map((link) => (
            <li key={link.label} className="border-b border-gray-100 last:border-0">
              <a
                href={link.href}
                onClick={(e) => {
                  e.preventDefault();
                  if (link.isPage) onPage(link.href);
                  else onAnchor(link.href);
                }}
                data-testid={`mobile-nav-${link.label.toLowerCase().replace(/\s+/g, '-')}`}
                className="flex items-center justify-between py-4 text-base font-semibold text-gray-900 hover:text-[#00BCE4] active:bg-gray-50 transition-colors rounded-md px-2 -mx-2"
                style={{ fontFamily: 'Outfit, sans-serif' }}
              >
                <span>{link.label}</span>
                <ChevronRight size={18} className="text-gray-300" />
              </a>
              {link.children && (
                <ul className="pl-3 pb-3 space-y-0.5 border-l-2 border-[#00BCE4]/20 ml-1">
                  {link.children.map((child) => (
                    <li key={child.label}>
                      <a
                        href={child.href}
                        onClick={(e) => { e.preventDefault(); child.isPage ? onPage(child.href) : onAnchor(child.href); }}
                        data-testid={`mobile-nav-child-${child.label.toLowerCase().replace(/\s+/g, '-')}`}
                        className="block py-2 px-3 text-sm text-gray-500 hover:text-[#00BCE4] active:bg-gray-50 rounded transition-colors"
                        style={{ fontFamily: 'Inter, sans-serif' }}
                      >
                        {child.label}
                      </a>
                    </li>
                  ))}
                </ul>
              )}
            </li>
          ))}
        </ul>

        {/* Contact info block */}
        <div className="mt-6 pt-6 border-t border-gray-100 space-y-2 text-xs text-gray-500" style={{ fontFamily: 'Inter, sans-serif' }}>
          <p className="font-semibold text-gray-400 uppercase tracking-wider text-[10px]" style={{ fontFamily: 'Outfit, sans-serif' }}>
            Get in touch
          </p>
          <a href="tel:+441245231670" className="block hover:text-[#00BCE4] transition-colors">+44 (0)1245 231670</a>
          <a href="mailto:info@imprint-mis.co.uk" className="block hover:text-[#00BCE4] transition-colors">info@imprint-mis.co.uk</a>
          <p className="text-gray-400 leading-snug">27 Fairycroft Road, Saffron Walden, CB10 1LZ</p>
        </div>
      </nav>

      {/* Sticky CTA */}
      <div className="flex-shrink-0 px-5 py-4 bg-white border-t border-gray-100 shadow-[0_-4px_12px_rgba(0,0,0,0.04)]">
        <a
          href="#contact"
          onClick={(e) => { e.preventDefault(); onCtaClick(); }}
          data-testid="mobile-menu-cta"
          className="btn-primary w-full justify-center text-xs"
        >
          Request a Demo
        </a>
      </div>
    </div>,
    document.body,
  );
};

export default MobileMenu;
